import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { EmailAttachment } from '@/types/email';

// Types
export interface Recipient {
    name?: string;
    email: string;
}

export interface ComposerData {
    id?: string;
    to?: Recipient[];
    cc?: Recipient[];
    bcc?: Recipient[];
    subject?: string;
    body?: string;
    /** Attachments already persisted on this draft (server metadata only — no local File blob). Used to hydrate the composer's attachment state on reopen so a save right after reopening doesn't wipe them. */
    attachments?: EmailAttachment[];
    /** Read-only quoted history (original message) rendered separately from the editable body. */
    quotedHtml?: string;
    replyToId?: string;
    /** RFC Message-ID of the email being replied to — sent as the outgoing In-Reply-To header, distinct from replyToId (our own row id, used for UI lookups). */
    replyToMessageId?: string;
    threadId?: string;
    isReplyToSent?: boolean;
    isReplyAll?: boolean;
    isForward?: boolean;
    isConfidential?: boolean;
    expiresAt?: string;
    requireAccessCode?: boolean;
}

export interface ComposerInstance {
    id: string;
    minimized: boolean;
    data?: ComposerData;
}

interface ComposerState {
    composers: ComposerInstance[];
    activeComposerId: string | null;
}

// Initial state
const initialState: ComposerState = {
    composers: [],
    activeComposerId: null,
};

// Helper to generate unique IDs
const generateComposerId = (): string => {
    return `composer-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
};

// Slice
const composerSlice = createSlice({
    name: 'composer',
    initialState,
    reducers: {
        openComposer: {
            reducer: (state, action: PayloadAction<{ id: string; composerData?: ComposerData }>) => {
                const { id, composerData } = action.payload;

                // Minimize all existing composers
                state.composers.forEach((composer) => {
                    composer.minimized = true;
                });

                // Add new composer — composerData.id is the backend draft ID (only set for
                // existing drafts). For new emails composerData is undefined, so draftId
                // never gets a fake frontend UUID.
                state.composers.push({
                    id,
                    minimized: false,
                    data: composerData,
                });

                state.activeComposerId = id;
            },
            prepare: (data?: ComposerData) => {
                const generatedId = generateComposerId();
                // Re-use the real draft ID as the composer key so that clicking the same
                // draft a second time replaces the existing composer instead of opening a
                // duplicate. For new emails data?.id is undefined, so the generated key is used.
                const id = data?.id || generatedId;
                return { payload: { id, composerData: data } };
            },
        },

        closeComposer: (state, action: PayloadAction<string>) => {
            state.composers = state.composers.filter(
                (composer) => composer.id !== action.payload
            );

            // Update active composer
            if (state.activeComposerId === action.payload) {
                state.activeComposerId = state.composers.length > 0
                    ? state.composers[state.composers.length - 1].id
                    : null;
            }
        },

        toggleMinimize: (state, action: PayloadAction<string>) => {
            const targetComposer = state.composers.find(
                (c) => c.id === action.payload
            );

            if (!targetComposer) return;

            const isMaximizing = targetComposer.minimized;

            if (isMaximizing) {
                // Minimize all others, maximize this one
                state.composers.forEach((composer) => {
                    composer.minimized = composer.id !== action.payload;
                });
                state.activeComposerId = action.payload;
            } else {
                // Just minimize this one
                targetComposer.minimized = true;
            }
        },

        updateComposer: (
            state,
            action: PayloadAction<{ id: string } & Partial<ComposerData>>
        ) => {
            // Exclude the composer instance key from the data merge so that
            // data.id always reflects the backend draft ID (never a frontend UUID).
            const { id: composerKey, ...dataUpdates } = action.payload;
            const composer = state.composers.find((c) => c.id === composerKey);
            if (composer) {
                composer.data = { ...composer.data, ...dataUpdates } as ComposerData;
            }
        },

        setActiveComposer: (state, action: PayloadAction<string>) => {
            state.activeComposerId = action.payload;
        },

        clearAllComposers: (state) => {
            state.composers = [];
            state.activeComposerId = null;
        },
    },
});

// Export actions
export const {
    openComposer,
    closeComposer,
    toggleMinimize,
    updateComposer,
    setActiveComposer,
    clearAllComposers,
} = composerSlice.actions;

// Selectors
export const selectComposers = (state: { composer: ComposerState }) =>
    state.composer.composers;

export const selectActiveComposerId = (state: { composer: ComposerState }) =>
    state.composer.activeComposerId;

export const selectComposerById = (id: string) => (state: { composer: ComposerState }) =>
    state.composer.composers.find((c) => c.id === id);

// Export reducer
export default composerSlice.reducer;
